import { RefObject, useEffect, useLayoutEffect, useRef } from 'react';
import { ProgrammaticScrollGuard } from './use-programmatic-scroll-guard';
import { ThreadGestureClock } from './use-thread-gesture-clock';

const CORRECTION_WINDOW_MS = 600;
const USER_INPUT_GRACE_MS = 150;

/*
  Keeps the reader's place when a page of older messages merges into the reversed
  thread.

  The thread is column-reverse, where scrollTop is measured from the BOTTOM — so
  "the reader's content did not move" is exactly "scrollTop did not change", no
  matter where in the DOM the new page landed (new sibling groups or growth inside
  an existing group). The enemy is native scroll anchoring: pinned near the top, the
  browser glues the topmost box and shifts scrollTop by the whole batch height,
  dragging the reader and (being back inside the load-more threshold) inviting the
  next fetch.

  The pre-merge scrollTop cannot be snapshotted in the merge commit: the new rows'
  own layout effects (height measurements) run first, force a layout, and anchoring
  has already applied its shift by then. Instead the last scroll position the USER
  produced is tracked continuously from scroll events; when the oldest loaded
  message gets older (a prepend merged), a short window opens during which any
  non-user movement of scrollTop is put back to that tracked value. A wheel, touch,
  or mouse gesture closes the window immediately: the user always wins.
*/
interface UsePrependScrollLockParams {
  chatRef: RefObject<HTMLDivElement | null>;
  oldestMessageTimestamp: string | null;
  clock: ThreadGestureClock;
  scrollGuard: ProgrammaticScrollGuard;
}

export function usePrependScrollLock({
  chatRef,
  oldestMessageTimestamp,
  clock,
  scrollGuard,
}: UsePrependScrollLockParams): void {
  const prevOldestRef = useRef<string | null>(oldestMessageTimestamp);
  const trackedScrollTopRef = useRef<number | null>(null);
  const windowUntilRef = useRef(0);

  useLayoutEffect(() => {
    const prev = prevOldestRef.current;
    prevOldestRef.current = oldestMessageTimestamp;

    if (!prev || !oldestMessageTimestamp) return;

    const prevMs = new Date(prev).getTime();
    const nextMs = new Date(oldestMessageTimestamp).getTime();
    if (Number.isNaN(prevMs) || Number.isNaN(nextMs) || nextMs >= prevMs) return;

    // an older page just merged: guard the reader's tracked position for a moment
    windowUntilRef.current = performance.now() + CORRECTION_WINDOW_MS;
  });

  useEffect(
    () =>
      clock.subscribe(() => {
        // a live gesture takes over — stop guarding the old position
        windowUntilRef.current = 0;
      }),
    [clock],
  );

  useEffect(() => {
    const container = chatRef.current;
    if (!container) return;

    const onScroll = () => {
      // a position the app scrolled to on purpose becomes the reader's place
      if (scrollGuard.isSuppressedRef.current) {
        trackedScrollTopRef.current = container.scrollTop;
        return;
      }

      const now = performance.now();
      const inWindow = now <= windowUntilRef.current;
      const userIsScrolling = now - clock.lastGestureAt() <= USER_INPUT_GRACE_MS;

      if (inWindow && !userIsScrolling) {
        const tracked = trackedScrollTopRef.current;
        if (tracked !== null && Math.abs(container.scrollTop - tracked) > 1) {
          container.scrollTop = tracked;
          return; // keep the tracked value: this assignment re-enters onScroll
        }
      }

      trackedScrollTopRef.current = container.scrollTop;
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', onScroll);
    };
  }, [chatRef, clock, scrollGuard]);
}
